import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';
import Container from 'components/Container';
import { H3 } from 'components/Heading';
import Loading from 'components/Loading';

const ProjectManagementContainer = styled.div`
  display: flex;
  gap: 30px;
  margin-top: 20px;
  height: calc(100vh - 200px);

  @media (max-width: 768px) {
    flex-direction: column;
    height: auto;
  }
`;

const Sidebar = styled.div`
  width: 300px;
  background: white;
  border-radius: 8px;
  padding: 20px;
  overflow-y: auto;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  max-height: 80vh;

  @media (max-width: 768px) {
    width: 100%;
    max-height: 300px;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  background: white;
  border-radius: 8px;
  padding: 20px;
  overflow-y: auto;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  max-height: 80vh;

  @media (max-width: 768px) {
    width: 100%;
    max-height: none;
    min-height: 500px;
  }
`;

const FolderItem = styled.div`
  padding: 10px;
  cursor: pointer;
  border-radius: 4px;
  margin-bottom: 5px;
  background: ${(props) => (props.active ? '#f0f8ff' : 'transparent')};
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: #f5f5f5;
  }

  font-weight: ${(props) => (props.isFolder ? '600' : 'normal')};
`;

const FileItem = styled.div`
  padding: 8px 8px 8px 24px;
  cursor: pointer;
  border-radius: 4px;
  margin-bottom: 3px;
  background: ${(props) => (props.active ? '#e6f3ff' : 'transparent')};
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: #f5f5f5;
  }
`;

const IframeContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;

  iframe {
    width: 100%;
    height: 100%;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    min-height: 600px;
  }
`;

const NoContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 400px;
  color: #666;
  font-size: 18px;
  text-align: center;
`;

const MarkdownContainer = styled.div`
  padding: 20px;
  max-width: 900px;
  margin: 0 auto;

  h1 {
    font-size: 2.5em;
    margin: 0.67em 0;
    font-weight: bold;
    border-bottom: 2px solid #e0e0e0;
    padding-bottom: 0.3em;
  }

  h2 {
    font-size: 2em;
    margin: 0.83em 0;
    font-weight: bold;
    border-bottom: 1px solid #e0e0e0;
    padding-bottom: 0.3em;
  }

  h3 {
    font-size: 1.5em;
    margin: 1em 0;
    font-weight: bold;
  }

  h4 {
    font-size: 1.25em;
    margin: 1.33em 0;
    font-weight: bold;
  }

  h5 {
    font-size: 1em;
    margin: 1.5em 0;
    font-weight: bold;
  }

  h6 {
    font-size: 0.875em;
    margin: 1.5em 0;
    font-weight: bold;
    color: #666;
  }

  ul,
  ol {
    margin: 1em 0;
    padding-left: 2em;
  }

  li {
    margin: 0.5em 0;
    line-height: 1.6;
  }

  p {
    margin: 1em 0;
    line-height: 1.6;
  }

  code {
    background: #f4f4f4;
    padding: 2px 6px;
    border-radius: 3px;
    font-family: 'Courier New', Courier, monospace;
    font-size: 0.9em;
  }

  pre {
    background: #f4f4f4;
    padding: 15px;
    border-radius: 6px;
    overflow-x: auto;

    code {
      background: none;
      padding: 0;
      font-size: 0.9em;
    }
  }

  blockquote {
    border-left: 4px solid #ddd;
    padding-left: 20px;
    margin: 1em 0;
    color: #666;
    font-style: italic;
  }

  table {
    border-collapse: collapse;
    width: 100%;
    margin: 1em 0;
  }

  th,
  td {
    border: 1px solid #ddd;
    padding: 10px;
    text-align: left;
  }

  th {
    background: #f4f4f4;
    font-weight: bold;
  }

  a {
    color: #0066cc;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  img {
    max-width: 100%;
    height: auto;
  }

  hr {
    border: none;
    border-top: 1px solid #e0e0e0;
    margin: 2em 0;
  }

  strong {
    font-weight: bold;
  }

  em {
    font-style: italic;
  }
`;

const FileViewer = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState(['root']);
  const [markdownContent, setMarkdownContent] = useState('');
  const [loading, setLoading] = useState(false);

  // Static file structure based on your project-management folder
  const fileStructure = {
    customers: {
      type: 'folder',
      children: {
        'customer-detail.html': {
          type: 'file',
          path: '/project-management/customers/customer-detail.html',
        },
        'customer-prd.md': {
          type: 'file',
          path: '/project-management/customers/customer-prd.md',
        },
        'customers.html': {
          type: 'file',
          path: '/project-management/customers/customers.html',
        },
        'fan-loyalty.html': {
          type: 'file',
          path: '/project-management/customers/fan-loyalty.html',
        },
        'geographic.html': {
          type: 'file',
          path: '/project-management/customers/geographic.html',
        },
        explorations: {
          type: 'folder',
          children: {
            'activity-timeline-variations.html': {
              type: 'file',
              path: '/project-management/customers/explorations/activity-timeline-variations.html',
            },
            'customer-overview-charts.html': {
              type: 'file',
              path: '/project-management/customers/explorations/customer-overview-charts.html',
            },
            'customer-overview-examples.html': {
              type: 'file',
              path: '/project-management/customers/explorations/customer-overview-examples.html',
            },
            'customer-overview-variations.html': {
              type: 'file',
              path: '/project-management/customers/explorations/customer-overview-variations.html',
            },
            'customer-segmentation-examples.html': {
              type: 'file',
              path: '/project-management/customers/explorations/customer-segmentation-examples.html',
            },
            'customer-segmentation-hierarchical.html': {
              type: 'file',
              path: '/project-management/customers/explorations/customer-segmentation-hierarchical.html',
            },
            'fan-loyalty-stat-variations.html': {
              type: 'file',
              path: '/project-management/customers/explorations/fan-loyalty-stat-variations.html',
            },
            'header-variations.html': {
              type: 'file',
              path: '/project-management/customers/explorations/header-variations.html',
            },
            'mobile-table-card-examples.html': {
              type: 'file',
              path: '/project-management/customers/explorations/mobile-table-card-examples.html',
            },
            'search-examples.html': {
              type: 'file',
              path: '/project-management/customers/explorations/search-examples.html',
            },
          },
        },
      },
    },
    discounts: {
      type: 'folder',
      children: {
        'create-dscount.html': {
          type: 'file',
          path: '/project-management/discounts/create-dscount.html',
        },
        'prd-discount-promo-system.md': {
          type: 'file',
          path: '/project-management/discounts/prd-discount-promo-system.md',
        },
        'promo-tool-changes-report.md': {
          type: 'file',
          path: '/project-management/discounts/promo-tool-changes-report.md',
        },
        'updated_promo_prd.md': {
          type: 'file',
          path: '/project-management/discounts/updated_promo_prd.md',
        },
        explorations: {
          type: 'folder',
          children: {
            'discount-selector-with-video.html': {
              type: 'file',
              path: '/project-management/discounts/explorations/discount-selector-with-video.html',
            },
            'pro-tip-examples.html': {
              type: 'file',
              path: '/project-management/discounts/explorations/pro-tip-examples.html',
            },
            'tab-examples.html': {
              type: 'file',
              path: '/project-management/discounts/explorations/tab-examples.html',
            },
          },
        },
      },
    },
    'pricing-calculator': {
      type: 'folder',
      children: {
        'pricing-calculator-orange.html': {
          type: 'file',
          path: '/project-management/pricing-calculator/pricing-calculator-orange.html',
        },
        explorations: {
          type: 'folder',
          children: {
            'slider-variations-showcase.html': {
              type: 'file',
              path: '/project-management/pricing-calculator/explorations/slider-variations-showcase.html',
            },
          },
        },
      },
    },
    'upcoming-events-email': {
      type: 'folder',
      children: {
        'credit-badge-options.html': {
          type: 'file',
          path: '/project-management/upcoming-events-email/credit-badge-options.html',
        },
        'divisions-options.html': {
          type: 'file',
          path: '/project-management/upcoming-events-email/divisions-options.html',
        },
        'featured-event-cards.html': {
          type: 'file',
          path: '/project-management/upcoming-events-email/featured-event-cards.html',
        },
        'multi-tier-pricing-options.html': {
          type: 'file',
          path: '/project-management/upcoming-events-email/multi-tier-pricing-options.html',
        },
        'no-image-consistent-radius.html': {
          type: 'file',
          path: '/project-management/upcoming-events-email/no-image-consistent-radius.html',
        },
        'pricing-options.html': {
          type: 'file',
          path: '/project-management/upcoming-events-email/pricing-options.html',
        },
        'special-callout-options.html': {
          type: 'file',
          path: '/project-management/upcoming-events-email/special-callout-options.html',
        },
        'upcoming-events-email-condensed.html': {
          type: 'file',
          path: '/project-management/upcoming-events-email/upcoming-events-email-condensed.html',
        },
        'upcoming-events-email-dark-blue.html': {
          type: 'file',
          path: '/project-management/upcoming-events-email/upcoming-events-email-dark-blue.html',
        },
        'upcoming-events-email-dark.html': {
          type: 'file',
          path: '/project-management/upcoming-events-email/upcoming-events-email-dark.html',
        },
        'upcoming-events-email-data-structure.json': {
          type: 'file',
          path: '/project-management/upcoming-events-email/upcoming-events-email-data-structure.json',
        },
        'upcoming-events-email-fixed.html': {
          type: 'file',
          path: '/project-management/upcoming-events-email/upcoming-events-email-fixed.html',
        },
        'upcoming-events-email-implementation-notes.md': {
          type: 'file',
          path: '/project-management/upcoming-events-email/upcoming-events-email-implementation-notes.md',
        },
        'upcoming-events-email-redesigned.html': {
          type: 'file',
          path: '/project-management/upcoming-events-email/upcoming-events-email-redesigned.html',
        },
        'upcoming-events-email-two-sections.html': {
          type: 'file',
          path: '/project-management/upcoming-events-email/upcoming-events-email-two-sections.html',
        },
        'upcoming-events-enhanced-two-sections.html': {
          type: 'file',
          path: '/project-management/upcoming-events-email/upcoming-events-enhanced-two-sections.html',
        },
      },
    },
    'style-guide.md': {
      type: 'file',
      path: '/project-management/style-guide.md',
    },
  };

  const handleFileClick = async (file) => {
    setSelectedFile(file);

    // If it's a markdown file, fetch its content
    if (file.path.endsWith('.md')) {
      setLoading(true);
      try {
        const response = await fetch(file.path);
        const text = await response.text();
        setMarkdownContent(text);
      } catch (error) {
        console.error('Error fetching markdown file:', error);
        setMarkdownContent(
          '# Error\n\nFailed to load markdown file. Please try again.'
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const toggleFolder = (folderPath) => {
    setExpandedFolders((prev) =>
      prev.includes(folderPath)
        ? prev.filter((p) => p !== folderPath)
        : [...prev, folderPath]
    );
  };

  const renderFileTree = (node, path = '', depth = 0) => {
    if (!node) return null;

    return Object.entries(node).map(([name, item]) => {
      const fullPath = path ? `${path}/${name}` : name;

      if (item.type === 'folder') {
        const isExpanded = expandedFolders.includes(fullPath);
        return (
          <div key={fullPath}>
            <FolderItem
              isFolder
              onClick={() => toggleFolder(fullPath)}
              style={{ paddingLeft: `${depth * 20}px` }}
            >
              <span>{isExpanded ? '📂' : '📁'}</span>
              <span>{name}</span>
            </FolderItem>
            {isExpanded &&
              item.children &&
              renderFileTree(item.children, fullPath, depth + 1)}
          </div>
        );
      } else if (item.type === 'file') {
        const isHtml = name.endsWith('.html');
        const isMd = name.endsWith('.md');
        const isJson = name.endsWith('.json');

        if (!isHtml && !isMd && !isJson) return null;

        let icon = '📄';
        if (isMd) icon = '📝';
        if (isJson) icon = '📊';

        return (
          <FileItem
            key={fullPath}
            active={selectedFile?.path === item.path}
            onClick={() => handleFileClick(item)}
            style={{ paddingLeft: `${(depth + 1) * 20}px` }}
          >
            <span>{icon}</span>
            <span>{name}</span>
          </FileItem>
        );
      }
      return null;
    });
  };

  const renderContent = () => {
    if (!selectedFile) {
      return (
        <NoContent>
          <div>
            <h3>Welcome to Project Management Files</h3>
            <p>Select a file from the sidebar to view its content</p>
          </div>
        </NoContent>
      );
    }

    if (loading) {
      return <Loading />;
    }

    // For markdown files, render with ReactMarkdown
    if (selectedFile.path.endsWith('.md')) {
      return (
        <MarkdownContainer>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {markdownContent}
          </ReactMarkdown>
        </MarkdownContainer>
      );
    }

    // For JSON files
    if (selectedFile.path.endsWith('.json')) {
      return (
        <IframeContainer>
          <NoContent>
            <div>
              <h3>{selectedFile.path.split('/').pop()}</h3>
              <p>JSON viewer coming soon. For now, open the file directly:</p>
              <p>
                <code>{selectedFile.path}</code>
              </p>
            </div>
          </NoContent>
        </IframeContainer>
      );
    }

    // For HTML files, display in iframe
    return (
      <IframeContainer>
        <iframe
          src={selectedFile.path}
          title={selectedFile.path}
          sandbox="allow-same-origin allow-scripts"
        />
      </IframeContainer>
    );
  };

  return (
    <Container>
      <H3>Project Management Documentation</H3>
      <ProjectManagementContainer>
        <Sidebar>
          <h3 style={{ marginTop: 0, marginBottom: 15, fontSize: '18px' }}>
            Browse Files
          </h3>
          {renderFileTree(fileStructure)}
        </Sidebar>
        <ContentArea>{renderContent()}</ContentArea>
      </ProjectManagementContainer>
    </Container>
  );
};

export default FileViewer;
